/**
 * Fixed-timestep loop.
 *
 * Rendering runs at whatever the display does; gameplay always advances in
 * exact 1/120 s slices. The leftover accumulator becomes the interpolation
 * alpha, so a 60 Hz and a 144 Hz machine see the same simulation.
 */

import { FIXED_TIMESTEP, MAX_FRAME_TIME, MAX_STEPS_PER_FRAME } from '../config/gameplay';

export interface GameLoopCallbacks {
  /** Called once per frame *before* any fixed step: input, actions, UI intent. */
  preUpdate: (frameSeconds: number) => void;
  /** Called 0..MAX_STEPS_PER_FRAME times per frame, always with dt = 1/120. */
  step: (dt: number) => void;
  /** alpha is 0..1 between the previous and the current simulation state. */
  render: (alpha: number, frameSeconds: number) => void;
}

export class GameLoop {
  private running = false;
  private handle = 0;
  private lastTime = 0;
  private accumulator = 0;
  private frameSeconds = 0;
  private fps = 0;
  private fpsCounter = 0;
  private fpsElapsed = 0;
  private stepsLastFrame = 0;

  constructor(private readonly callbacks: GameLoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.handle = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.handle);
  }

  get currentFps(): number {
    return this.fps;
  }

  get accumulatorSeconds(): number {
    return this.accumulator;
  }

  get lastFrameSeconds(): number {
    return this.frameSeconds;
  }

  get lastStepCount(): number {
    return this.stepsLastFrame;
  }

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    this.handle = requestAnimationFrame(this.frame);

    const rawDelta = (now - this.lastTime) / 1000;
    this.lastTime = now;
    this.frameSeconds = rawDelta;

    this.fpsCounter++;
    this.fpsElapsed += rawDelta;
    if (this.fpsElapsed >= 0.25) {
      this.fps = this.fpsCounter / this.fpsElapsed;
      this.fpsCounter = 0;
      this.fpsElapsed = 0;
    }

    this.callbacks.preUpdate(rawDelta);

    // Tab-outs and hitches must not fast-forward the simulation.
    this.accumulator += Math.min(rawDelta, MAX_FRAME_TIME);

    let steps = 0;
    while (this.accumulator >= FIXED_TIMESTEP && steps < MAX_STEPS_PER_FRAME) {
      this.callbacks.step(FIXED_TIMESTEP);
      this.accumulator -= FIXED_TIMESTEP;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) {
      // Give up on catching up rather than spiralling.
      this.accumulator = 0;
    }
    this.stepsLastFrame = steps;

    this.callbacks.render(this.accumulator / FIXED_TIMESTEP, rawDelta);
  };
}
